/**
 * Reading the `exp` claim out of a JWT, without a library.
 *
 * Lives in its own module because both the API client and the auth store need it:
 * the client to decide when to refresh, the store to decide whether the session it
 * is holding is still alive when another tab claims it is over. Importing it from
 * the client would make the store and the client circular.
 */
export function getJWTExp(token: string): number | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    // JWTs are base64url ("-" and "_"); atob() only accepts standard base64 and
    // throws on those characters. Roughly half of all tokens contain one, and the
    // thrown error used to surface as "expiry unknown" — which made the scheduler
    // skip its proactive refresh and, worse, made a still-valid refresh token look
    // spent, logging the user out on the very race this module now tolerates.
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when the token is readable and has not expired yet. Unreadable = not live. */
export function jwtStillLive(token: string | null | undefined): boolean {
  if (!token) return false;
  const exp = getJWTExp(token);
  return exp !== null && exp * 1000 > Date.now();
}
